import { BankStatementRuleConditionKindEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.condition.kind.enum';
import { BankStatementRuleConditionKindEnumI18n } from '@zaoblako/shared/modules/document/i18n/bank.statement.rule.condition.kind.enum.i18n';
import { BankStatementRuleConditionTypeEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.condition.type.enum';
import { BankStatementRuleConditionTypeEnumI18n } from '@zaoblako/shared/modules/document/i18n/bank.statement.rule.condition.type.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchemaFactory } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';

import * as yup from 'yup';

/** Bank statement rule create and update API's condition request body - DTO signature */
export interface BankStatementRuleConditionFormInterface {
  /** Condition kind - which fields to compare */
  kind: BankStatementRuleConditionKindEnum;
  /** Condition type - how fields should be compared */
  type: BankStatementRuleConditionTypeEnum;
  /**
   * If {@link BankStatementRuleConditionFormInterface.kind} equals {@link BankStatementRuleConditionKindEnum.DATE},
   * then documents will be compared by date with this field
   */
  date?: Date;
  /**
   * If {@link BankStatementRuleConditionFormInterface.kind} equals {@link BankStatementRuleConditionKindEnum.AMOUNT},
   * then documents will be compared by amount with this field
   */
  amount?: number;
  /**
   * If {@link BankStatementRuleConditionFormInterface.kind} equals {@link BankStatementRuleConditionKindEnum.COMPANY},
   * then documents will be compared by company TIN (rus. "ИНН") and, optionally, TRRC (rus. "КПП")
   * with {@link CompanyFindInterface.inn} and, optionally, {@link CompanyFindInterface.kpp} fields from this field
   */
  company?: CompanyFindInterface;
  /**
   * If {@link BankStatementRuleConditionFormInterface.kind} equals {@link BankStatementRuleConditionKindEnum.COMPANY_BANK_DETAILS},
   * then documents will be compared by checking account number and RCBIC (rus. "БИК")
   * with {@link BankDetailsFindInterface.checkingAccount} and {@link BankDetailsFindInterface.bank} fields from this field
   */
  companyBankDetails?: BankDetailsFindInterface;
  /**
   * If {@link BankStatementRuleConditionFormInterface.kind} equals {@link BankStatementRuleConditionKindEnum.CONTRAGENT},
   * then documents will be compared by counterparty TIN (rus. "ИНН") and, optionally, TRRC (rus. "КПП")
   * with {@link ContragentFindInterface.inn} and, optionally, {@link ContragentFindInterface.kpp} fields from this field
   */
  contragent?: ContragentFindInterface;
}

const falseIfKindIsNot = (kind: BankStatementRuleConditionKindEnum) => (
  context: SerdeContextInterface<SerdeContextDataInterface, BankStatementRuleConditionFormInterface>,
): boolean => context?.value?.kind !== kind;

/** Bank statement rule create and update API's condition request body - DTO schema */
export const BankStatementRuleConditionFormDto = SerdeDtoClass.create<BankStatementRuleConditionFormInterface>(
  DocumentDtoKeysEnum.BANK_STATEMENT_RULE_CONDITION_FORM_DTO,
  {
    kind: {
      label: 'Вид условия',
      hint: 'Определяет, что будет сравниваться',
      serializer: commonEnumValidator(BankStatementRuleConditionKindEnum, BankStatementRuleConditionKindEnumI18n)
    },
    type: {
      label: 'Тип условия',
      hint: 'Определяет, как будет сравниваться',
      serializer: commonEnumValidator(BankStatementRuleConditionTypeEnum, BankStatementRuleConditionTypeEnumI18n)
    },
    date: {
      optional: falseIfKindIsNot(BankStatementRuleConditionKindEnum.DATE),
      label: 'Дата',
      serializer: yup.date()
    },
    amount: {
      optional: falseIfKindIsNot(BankStatementRuleConditionKindEnum.AMOUNT),
      label: 'Сумма',
      serializer: yup.number(),
    },
    company: {
      optional: falseIfKindIsNot(BankStatementRuleConditionKindEnum.COMPANY),
      label: 'Компания',
      serializer: entityLikeSchemaFactory<SerdeContextDataInterface, BankStatementRuleConditionFormInterface>(
        context => context?.value?.kind === BankStatementRuleConditionKindEnum.COMPANY,
      ),
      checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
    },
    companyBankDetails: {
      optional: falseIfKindIsNot(BankStatementRuleConditionKindEnum.COMPANY_BANK_DETAILS),
      label: 'Рассчетный счет компании',
      serializer: entityLikeSchemaFactory<SerdeContextDataInterface, BankStatementRuleConditionFormInterface>(
        context => context?.value?.kind === BankStatementRuleConditionKindEnum.COMPANY_BANK_DETAILS,
      ),
      checkers: [ClientCheckerKeysEnum.BANK_DETAILS_ASSERT_EXISTS_BY_DTO],
    },
    contragent: {
      optional: falseIfKindIsNot(BankStatementRuleConditionKindEnum.CONTRAGENT),
      label: 'Контрагент',
      serializer: entityLikeSchemaFactory<SerdeContextDataInterface, BankStatementRuleConditionFormInterface>(
        context => context?.value?.kind === BankStatementRuleConditionKindEnum.CONTRAGENT,
      ),
      checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
    },
  },
);
